import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static values = {
    eventId: Number,
    selectedEventDayIds: Array,
    selectedFunctionId: Number,
    festivalDaysUrl: String,
    profilesUrl: String,
    defaultCategoryUrl: String,
    functionsUrl: String,
    userDataUrl: String,
    currentUserEmail: String,
    selfRequest: Boolean,
    identityPhotoAttached: Boolean,
    avatarMandatory: Boolean,
    initialIdentityLoaded: Boolean,
    noDaysText: String,
    selectAllDaysText: String,
    deselectAllDaysText: String,
    selfRequestWarning: String,
    keepIdentityOpen: Boolean,
    gdprSelfText: String,
    gdprOtherText: String,
  }

  connect() {
    this.avatarMandatory = this.avatarMandatoryValue
    this.loadedIdentityEmail = this.initialIdentityLoadedValue ? this.recipientEmailInput()?.value.trim() : null
    this.invalidHandler = (event) => {
      const details = event.target.closest(".accreditation-identity-details")
      if (details) details.open = true
    }
    this.element.addEventListener("invalid", this.invalidHandler, true)
    this.setupUserEmailAutocomplete()
    this.loadFestivalDays()

    if (!this.keepIdentityOpenValue) this.loadUserDataFromEmail()

    const profileSelect = this.profileSelect()
    if (profileSelect?.value) {
      this.updateForProfile()
    }
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.invalidHandler, true)
    this.userEmailSelect?.destroy()
  }

  setupUserEmailAutocomplete() {
    const input = this.recipientEmailInput()
    if (!input || input.readOnly || input.tomselect) return

    this.userEmailSelect = new TomSelect(input, {
      create: true,
      createOnBlur: true,
      maxItems: 1,
      persist: false,
      selectOnTab: true,
      closeAfterSelect: true,
      valueField: "value",
      labelField: "text",
      searchField: "text",
      maxOptions: 20,
      loadThrottle: 300,
      shouldLoad: (query) => query.length >= 3,
      load: (query, callback) => {
        const url = input.dataset.userOptionsUrl
        if (!url || query.length < 3) return callback()

        fetch(`${url}?q=${encodeURIComponent(query)}`, {
          headers: { "Accept": "application/json" },
          credentials: "same-origin"
        })
          .then((response) => response.json())
          .then((json) => callback(json))
          .catch(() => callback())
      },
      onChange: (value) => {
        input.value = value
        this.loadUserDataFromEmail()
      },
      render: {
        option: (data, escape) => `<div>${escape(data.text)}</div>`,
        item: (data, escape) => `<div>${escape(data.value)}</div>`,
        option_create: (data, escape) => `<div class="create">${escape(input.dataset.addText)} <strong>${escape(data.input)}</strong>&hellip;</div>`,
        no_results: (_data, escape) => `<div class="no-results">${escape(input.dataset.noResultsText)}</div>`
      }
    })

    this.userEmailSelect.wrapper.classList.add("enrollment-user-email-select")
    this.userEmailSelect.control_input.setAttribute("autocomplete", "new-password")
  }

  async eventChanged() {
    const eventId = this.eventSelect()?.value
    this.eventIdValue = Number(eventId || 0)
    if (this.categoryInput()) this.categoryInput().value = ""
    if (this.festivalDaysContainer()) this.festivalDaysContainer().innerHTML = ""
    this.updateAllDaysButton()
    if (this.profileSelect()) this.profileSelect().innerHTML = "<option value=''></option>"
    this.clearProfileDependentFields()
    this.updateSneasIdentityIdRequirement(false)
    this.avatarMandatory = true
    this.updatePhotoRequirement()
    if (!eventId) return

    await Promise.all([
      this.loadFestivalDays(),
      this.loadProfiles(),
    ])
    if (this.eventIdValue !== Number(eventId)) return

    const profileSelect = this.profileSelect()
    if (profileSelect?.value) {
      await this.updateForProfile()
    }
  }

  async emailChanged() {
    await this.loadUserDataFromEmail()
  }

  async fillWithMyInformation() {
    const input = this.recipientEmailInput()
    const email = this.currentUserEmailValue
    if (!input || !email) return

    this.selfRequestValue = true
    if (this.userEmailSelect) {
      this.userEmailSelect.addOption({ value: email, text: email })
      this.userEmailSelect.setValue(email, true)
    }
    input.value = email
    this.element.querySelectorAll(".hide_before_email").forEach((field) => {
      field.readOnly = false
      field.classList.remove("bg-light")
    })
    const photoInput = this.element.querySelector("input[type='file'][name='accreditation[user][identity_photo]']")
    if (photoInput) photoInput.disabled = false
    await this.loadUserDataFromEmail()
  }

  profileChanged() {
    this.updateForProfile()
  }

  identityNameChanged() {
    const summary = this.element.querySelector("[data-accreditation-form-target='identitySummary']")
    if (!summary) return

    const firstName = this.element.querySelector("input[name='accreditation[user][first_name]']")?.value.trim()
    const lastName = this.element.querySelector("input[name='accreditation[user][last_name]']")?.value.trim()
    summary.textContent = [firstName, lastName].filter(Boolean).join(" ")
  }

  profileSelect() {
    return this.element.querySelector("#accreditation_profile_id")
  }

  eventSelect() {
    return this.element.querySelector("#accreditation_event_id")
  }

  functionSelect() {
    return this.element.querySelector("#accreditation_function_id")
  }

  functionContainer() {
    return this.element.querySelector("#function_selection")
  }

  categoryInput() {
    return this.element.querySelector("input[name='accreditation[category]']")
  }

  festivalDaysContainer() {
    return this.element.querySelector("#fest_days_selection_content")
  }

  recipientEmailInput() {
    return this.element.querySelector("#accreditation_recipient_email") || this.element.querySelector("input[name='accreditation[recipient_email]']")
  }

  async loadUserDataFromEmail() {
    const input = this.recipientEmailInput()
    if (!input) return
    if (!this.hasUserDataUrlValue) return

    const email = input.value.trim()
    if (!email) {
      if (this.loadedIdentityEmail) this.clearUserIdentityFields()
      this.loadedIdentityEmail = null
      this.updateIdentitySummary()
      return
    }

    const isSelf = this.hasCurrentUserEmailValue && email.toLowerCase() === this.currentUserEmailValue.toLowerCase()
    if (!this.selfRequestValue && isSelf) {
      alert(this.selfRequestWarningValue)
      input.value = ""
      if (this.loadedIdentityEmail) this.clearUserIdentityFields()
      this.loadedIdentityEmail = null
      this.updateGdprLabel(false)
      this.updateIdentitySummary()
      return
    }
    this.updateGdprLabel(isSelf)

    const response = await fetch(`${this.userDataUrlValue}?email=${encodeURIComponent(email)}`)
    const data = await response.json()
    if (this.recipientEmailInput()?.value.trim() !== email) return

    const user = data?.response
    if (!user || Object.keys(user).length === 0) {
      if (this.loadedIdentityEmail && this.loadedIdentityEmail.toLowerCase() !== email.toLowerCase()) this.clearUserIdentityFields()
      this.loadedIdentityEmail = null
      this.updateIdentitySummary()
      return
    }

    this.loadedIdentityEmail = email
    this.identityPhotoAttachedValue = Boolean(user.identity_photo_attached)
    this.setUserIdentityFields(user, { lockIdentity: !isSelf })
    this.updateIdentitySummary(user, email)
    this.updatePhotoPreview(user.identity_photo_url)
    this.updatePhotoRequirement()
  }

  setUserIdentityFields(user, { lockIdentity } = { lockIdentity: true }) {
    const setVal = (selector, value) => {
      const el = this.element.querySelector(selector)
      if (!el) return
      el.value = value ?? ""
    }

    setVal("input[name='accreditation[user][first_name]']", user.first_name)
    setVal("input[name='accreditation[user][last_name]']", user.last_name)
    setVal("input[name='accreditation[user][birth_name]']", user.birth_name)
    setVal("input[name='accreditation[user][birth_date]']", user.birth_date)
    setVal("input[name='accreditation[user][birth_place]']", user.birth_place)

    const sexSelect = this.element.querySelector("select[name='accreditation[user][sex]']")
    if (sexSelect && user.sex !== null && user.sex !== undefined) sexSelect.value = user.sex

    const nationalitySelect = this.element.querySelector("select[name='accreditation[user][nationality_id]']")
    if (nationalitySelect && user.nationality_id) nationalitySelect.value = user.nationality_id

    const departmentSelect = this.element.querySelector("select[name='accreditation[user][department_id]']")
    if (departmentSelect && user.department_id) departmentSelect.value = user.department_id

    const birthCountrySelect = this.element.querySelector("select[name='accreditation[user][birth_country_id]']")
    if (birthCountrySelect && user.birth_country_id) birthCountrySelect.value = user.birth_country_id

    setVal("input[name='accreditation[user][category_activity]']", user.category_activity)

    const sexDocSelect = this.element.querySelector("select[name='accreditation[user][sneas_identity_document]']")
    if (sexDocSelect && user.sneas_identity_document !== null && user.sneas_identity_document !== undefined) {
      sexDocSelect.value = user.sneas_identity_document
    }
    setVal("input[name='accreditation[user][sneas_identity_id]']", user.sneas_identity_id)

    if (lockIdentity) {
      this.disableIdentityFields(user.identity_photo_status)
    }
  }

  updateGdprLabel(isSelf) {
    const text = this.element.querySelector("#accreditation_gdpr_text")
    if (!text) return

    text.textContent = isSelf ? this.gdprSelfTextValue : this.gdprOtherTextValue
  }

  updateIdentitySummary(user, email) {
    const summary = this.element.querySelector("[data-accreditation-form-target='identitySummary']")
    const details = this.element.querySelector(".accreditation-identity-details")
    if (summary) summary.textContent = user ? [user.first_name, user.last_name].filter(Boolean).join(" ").trim() || email : ""
    if (details && !this.keepIdentityOpenValue) details.open = !user
  }

  clearUserIdentityFields() {
    this.element.querySelectorAll(".accreditation-identity-fields .hide_before_email").forEach((field) => {
      field.value = ""
      field.readOnly = false
      field.classList.remove("bg-light")
    })

    const photoInput = this.element.querySelector("input[type='file'][name='accreditation[user][identity_photo]']")
    if (photoInput) {
      photoInput.value = ""
      photoInput.disabled = false
    }
    this.identityPhotoAttachedValue = false
    this.updatePhotoPreview()
    this.updatePhotoRequirement()
  }

  updatePhotoPreview(url) {
    const container = this.element.querySelector("#identity_photo_preview")
    if (!container) return

    container.replaceChildren()
    if (!url) return

    const image = document.createElement("img")
    image.src = url
    image.className = "rounded-circle accreditation-identity-photo-preview"
    container.appendChild(image)
  }

  disableIdentityFields(identityPhotoStatus) {
    const shouldDisablePhoto = identityPhotoStatus !== null && identityPhotoStatus !== undefined && String(identityPhotoStatus) !== "0"

    const identityFields = this.element.querySelectorAll(".hide_before_email")
    identityFields.forEach((el) => {
      if (el.tagName === "SELECT") {
        el.classList.add("bg-light")
        return
      }
      if (el.tagName === "INPUT") {
        if (el.type === "file") return
        el.readOnly = true
      }
    })

    const photoInput = this.element.querySelector("input[type='file'][name='accreditation[user][identity_photo]']")
    if (photoInput && shouldDisablePhoto) {
      photoInput.disabled = true
      photoInput.required = false
    }
  }

  async updateForProfile() {
    const profileId = this.profileSelect()?.value
    if (!profileId) {
      this.updateSneasIdentityIdRequirement(false)
      this.avatarMandatory = true
      this.updatePhotoRequirement()
      return
    }

    await Promise.all([
      this.loadFunctions(profileId),
      this.loadDefaultCategory(profileId),
    ])
  }

  async loadFestivalDays() {
    if (!this.hasFestivalDaysUrlValue) return
    if (!this.hasEventIdValue) return
    if (!this.eventIdValue) return

    const eventId = this.eventIdValue
    const response = await fetch(`${this.festivalDaysUrlValue}?event_id=${eventId}`)
    const data = await response.json()
    if (this.eventIdValue !== eventId) return
    const container = this.festivalDaysContainer()
    if (!container) return

    container.replaceChildren()
    const responseItems = data?.response || []

    if (responseItems.length === 0) {
      const empty = document.createElement("span")
      empty.className = "text-muted small"
      empty.textContent = this.noDaysTextValue
      container.appendChild(empty)
      this.updateAllDaysButton()
      return
    }

    const selectedIds = (this.hasSelectedEventDayIdsValue ? this.selectedEventDayIdsValue : []).map(String)

    responseItems.forEach((day) => {
      const input = document.createElement("input")
      input.className = "btn-check"
      input.type = "checkbox"
      input.id = `event_day_${day.id}`
      input.name = "accreditation[event_day_ids][]"
      input.value = day.id
      input.checked = selectedIds.length === 0 || selectedIds.includes(String(day.id))
      input.autocomplete = "off"

      const label = document.createElement("label")
      label.className = "btn btn-outline-dark accreditation-day-option"
      label.htmlFor = input.id
      label.textContent = day.label
      container.append(input, label)
    })

    this.updateAllDaysButton()
  }

  toggleAllDays() {
    const inputs = this.festivalDaysContainer()?.querySelectorAll("input[type='checkbox']") || []
    if (inputs.length === 0) return

    const shouldSelect = Array.from(inputs).some((input) => !input.checked)
    inputs.forEach((input) => { input.checked = shouldSelect })
    this.updateAllDaysButton()
  }

  updateAllDaysButton() {
    const button = this.element.querySelector("#accreditation_toggle_all_days")
    if (!button) return

    const inputs = this.festivalDaysContainer()?.querySelectorAll("input[type='checkbox']") || []
    button.disabled = inputs.length === 0
    if (inputs.length === 0) return

    button.textContent = Array.from(inputs).every((input) => input.checked) ? this.deselectAllDaysTextValue : this.selectAllDaysTextValue
  }

  async loadProfiles() {
    if (!this.hasProfilesUrlValue) return
    if (!this.hasEventIdValue) return
    if (!this.eventIdValue) return

    const eventId = this.eventIdValue
    const response = await fetch(`${this.profilesUrlValue}?event_id=${eventId}`)
    const data = await response.json()
    if (this.eventIdValue !== eventId) return
    const profiles = data?.response || []
    const select = this.profileSelect()
    if (!select) return

    select.innerHTML = ""

    const empty = document.createElement("option")
    empty.value = ""
    empty.textContent = ""
    select.appendChild(empty)

    profiles.forEach((profile) => {
      const opt = document.createElement("option")
      opt.value = profile.id
      opt.textContent = profile.name
      select.appendChild(opt)
    })

    this.clearProfileDependentFields()
  }

  clearProfileDependentFields() {
    const functionContainer = this.functionContainer()
    const functionSelect = this.functionSelect()

    if (functionContainer) functionContainer.classList.add("d-none")
    if (functionSelect) functionSelect.innerHTML = ""
  }

  async loadDefaultCategory(profileId) {
    const url = this.defaultCategoryUrlValue
    if (!url) return

    const eventId = this.eventIdValue
    const response = await fetch(`${url}?event_id=${eventId}&profile_id=${profileId}`)
    const data = await response.json()
    if (this.eventIdValue !== eventId || this.profileSelect()?.value !== profileId) return
    const category = data?.response || ""

    const input = this.categoryInput()
    if (input) input.value = category
  }

  async loadFunctions(profileId) {
    const url = this.functionsUrlValue
    if (!url) return

    const eventId = this.eventIdValue
    const response = await fetch(`${url}?event_id=${eventId}&profile_id=${profileId}`)
    const data = await response.json()
    if (this.eventIdValue !== eventId || this.profileSelect()?.value !== profileId) return
    const functions = data?.response || []

    this.avatarMandatory = data?.avatar_mandatory !== false
    this.updateSneasIdentityIdRequirement(Boolean(data?.sneas_identity_id_required))
    this.updatePhotoRequirement()

    const select = this.functionSelect()
    if (!select) return

    const previousValue = select.value || (this.hasSelectedFunctionIdValue ? String(this.selectedFunctionIdValue) : "")
    select.innerHTML = ""

    const container = this.functionContainer()
    if (functions.length === 0) {
      if (container) container.classList.add("d-none")
      select.required = false
      return
    }

    if (container) container.classList.remove("d-none")

    const empty = document.createElement("option")
    empty.value = ""
    empty.textContent = ""
    select.appendChild(empty)

    functions.forEach((fn) => {
      const opt = document.createElement("option")
      opt.value = fn.id
      opt.textContent = fn.name
      select.appendChild(opt)
    })

    if (previousValue) select.value = previousValue
  }

  updateSneasIdentityIdRequirement(required) {
    const input = this.element.querySelector("input[name='accreditation[user][sneas_identity_id]']")
    if (!input) return

    input.required = required
    this.element.querySelector("#accreditation_sneas_identity_required_marker")?.classList.toggle("d-none", !required)
  }

  updatePhotoRequirement() {
    const photoInput = this.element.querySelector("input[type='file'][name='accreditation[user][identity_photo]']")
    if (!photoInput) return

    const marker = this.element.querySelector("#accreditation_photo_required_marker")
    if (photoInput.disabled) {
      marker?.classList.add("d-none")
      return
    }

    photoInput.required = Boolean(this.avatarMandatory) && !this.identityPhotoAttachedValue
    marker?.classList.toggle("d-none", !photoInput.required)
  }
}
